import { useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ExportButtons, SearchBar, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Pencil, Trash2, X, Copy } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nom: "", categorie: "Dessert", prix_vente: "", cout_emballage: "", description: "", actif: true, recette: [] };

export default function Products() {
  const { isAdmin, can } = useAuth();
  const { data, loading, refetch } = useData("/products");
  const { data: stockItems } = useData("/stock");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [recSel, setRecSel] = useState("");
  const [recQty, setRecQty] = useState("");
  const [recUnit, setRecUnit] = useState("g");
  const [query, setQuery] = useState("");
  const UNITES = ["g", "kg", "piece", "litre", "ml"];

  const openNew = () => { setForm(EMPTY); setEditId(null); setRecSel(stockItems[0]?.id || ""); setRecQty(""); setRecUnit(stockItems[0]?.unite || "g"); setOpen(true); };
  const openEdit = (p) => {
    setForm({ nom: p.nom, categorie: p.categorie, prix_vente: p.prix_vente, cout_emballage: p.cout_emballage ?? "", description: p.description || "", actif: p.actif, recette: p.recette || [] });
    setEditId(p.id); setRecSel(stockItems[0]?.id || ""); setRecQty(""); setRecUnit(stockItems[0]?.unite || "g"); setOpen(true);
  };

  const onRecSel = (id) => {
    setRecSel(id);
    const s = stockItems.find((x) => x.id === id);
    if (s?.unite) setRecUnit(s.unite);
  };

  // Coût des ingrédients = somme (quantité × prix d'achat de l'ingrédient au labo)
  const ingCost = (r) => {
    const s = stockItems.find((x) => x.id === r.stock_item_id || x.nom === r.nom);
    return (r.quantite || 0) * (s?.cout_unitaire || 0);
  };
  const coutIngredients = form.recette.reduce((a, r) => a + ingCost(r), 0);
  const coutEmballage = parseFloat(form.cout_emballage || 0);
  const coutRevient = coutIngredients + coutEmballage;
  const prixVente = parseFloat(form.prix_vente || 0);
  const margeDH = prixVente - coutRevient;
  const margePct = prixVente ? (margeDH / prixVente) * 100 : 0;

  const addRecipe = () => {
    const s = stockItems.find((x) => x.id === recSel);
    if (!s || !recQty || parseFloat(recQty) <= 0) return;
    if (form.recette.some((r) => r.stock_item_id === s.id)) return toast.error("Ingrédient déjà ajouté");
    setForm({ ...form, recette: [...form.recette, { stock_item_id: s.id, nom: s.nom, quantite: parseFloat(recQty), unite: recUnit }] });
    setRecQty("");
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, prix_vente: parseFloat(form.prix_vente), cout_emballage: parseFloat(form.cout_emballage || 0), cout_matiere: 0 };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Produit modifié" : "Produit ajouté");
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.nom} ?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Produit supprimé"); refetch();
  };

  const duplicate = async (p) => {
    try {
      await api.post("/products", {
        nom: `${p.nom} (copie)`,
        categorie: p.categorie,
        prix_vente: p.prix_vente,
        cout_emballage: p.cout_emballage || 0,
        cout_matiere: 0,
        description: p.description || "",
        actif: p.actif,
        recette: (p.recette || []).map((r) => ({ stock_item_id: r.stock_item_id, nom: r.nom, quantite: r.quantite, unite: r.unite || "" })),
      });
      toast.success(`"${p.nom}" dupliqué`); refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const cols = [
    { header: "Produit", value: (r) => r.nom },
    { header: "Catégorie", value: (r) => r.categorie },
    { header: "Prix vente (DH)", value: (r) => r.prix_vente },
    { header: "Coût revient (DH)", value: (r) => r.cout_matiere },
    { header: "Marge (DH)", value: (r) => r.marge },
    { header: "Marge %", value: (r) => r.marge_pct + "%" },
  ];

  const margeColor = (pct) => (pct >= 60 ? "#4CAF50" : pct >= 40 ? "#D4AF37" : "#FF9800");

  const q = query.trim().toLowerCase();
  const filtered = q
    ? data.filter((p) => [p.nom, p.categorie, p.description].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : data;

  return (
    <div>
      <PageHeader title="Produits & Marges" subtitle="Prix de vente, coût matière et marge par produit.">
        {can("produits", "export") && (
          <ExportButtons
            onExcel={() => exportExcel("produits_tiramessi", cols, data)}
            onPDF={() => exportPDF("Produits TIRAMESSI", cols, data)}
          />
        )}
        {can("produits", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-product-btn"><Plus size={16} /> Nouveau produit</button>}
      </PageHeader>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucun produit." /> : (
        <>
          <SearchBar value={query} onChange={setQuery} placeholder="Rechercher un produit, une catégorie..." testid="product-search" count={filtered.length} total={data.length} />
          {filtered.length === 0 ? <Empty text="Aucun résultat pour cette recherche." /> : (
          <div className="tm-card overflow-x-auto" data-testid="products-table">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Produit</th><th>Catégorie</th><th className="text-right">Prix vente</th>
                <th className="text-right">Coût revient</th><th className="text-right">Marge</th>
                <th className="text-right">Marge %</th>{(can("produits", "edit") || can("produits", "delete") || can("produits", "create")) && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`}>
                  <td className="font-semibold">{p.nom}</td>
                  <td><span className="tm-badge" style={{ background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>{p.categorie}</span></td>
                  <td className="text-right tabular">{MAD(p.prix_vente)}</td>
                  <td className="text-right tabular text-[var(--tm-muted)]">{MAD(p.cout_matiere)}{p.alerte_cout && <span className="block text-[11px] font-semibold" style={{ color: "#F44336" }} data-testid={`product-cost-alert-${p.id}`}>⚠ coût &gt; prix de vente</span>}</td>
                  <td className="text-right tabular font-semibold" style={{ color: margeColor(p.marge_pct) }}>{MAD(p.marge)}</td>
                  <td className="text-right tabular" style={{ color: margeColor(p.marge_pct) }}>{p.marge_pct}%</td>
                  {(can("produits", "edit") || can("produits", "delete") || can("produits", "create")) && (
                    <td className="text-right whitespace-nowrap">
                      {can("produits", "edit") && <button onClick={() => openEdit(p)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)] mr-3" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>}
                      {can("produits", "create") && <button onClick={() => duplicate(p)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)] mr-3" data-testid={`duplicate-product-${p.id}`} title="Dupliquer"><Copy size={16} /></button>}
                      {can("produits", "delete") && <button onClick={() => remove(p)} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier le produit" : "Nouveau produit"} testid="product-modal">
        <form onSubmit={save}>
          <Field label="Nom"><input className="tm-input" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="product-nom" /></Field>
          <Field label="Catégorie">
            <select className="tm-input" value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} data-testid="product-categorie">
              <option>Signature</option><option>Dessert</option><option>Pâtisserie</option><option>Boisson</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prix de vente (DH)"><input type="number" step="0.01" className="tm-input" required value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} data-testid="product-prix" /></Field>
            <Field label="Coût emballage (DH / unité)"><input type="number" step="0.01" className="tm-input" value={form.cout_emballage} onChange={(e) => setForm({ ...form, cout_emballage: e.target.value })} data-testid="product-emballage" /></Field>
          </div>
          <Field label="Description"><input className="tm-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>

          <div className="mb-4">
            <span className="block text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Recette — ingrédients du Laboratoire (coût calculé automatiquement)</span>
            <select className="tm-input mb-2" value={recSel} onChange={(e) => onRecSel(e.target.value)} data-testid="recipe-ingredient">
              {stockItems.map((s) => <option key={s.id} value={s.id}>{s.nom} ({s.unite}) — {MAD(s.cout_unitaire)}/{s.unite}</option>)}
            </select>
            <div className="grid grid-cols-[1fr_6rem_auto] gap-2 items-center mb-2">
              <input type="number" step="0.001" placeholder="Quantité" className="tm-input" value={recQty} onChange={(e) => setRecQty(e.target.value)} data-testid="recipe-qty" />
              <select className="tm-input" value={recUnit} onChange={(e) => setRecUnit(e.target.value)} data-testid="recipe-unit">
                {UNITES.map((u) => <option key={u}>{u}</option>)}
              </select>
              <button type="button" className="tm-btn-ghost tm-btn" onClick={addRecipe} data-testid="recipe-add"><Plus size={16} /> Ajouter</button>
            </div>
            {form.recette.length > 0 && (
              <div className="tm-card p-3" style={{ background: "var(--tm-bg)" }}>
                {form.recette.map((r, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1 text-sm">
                    <span>{r.nom} <span className="text-[var(--tm-muted)]">— {r.quantite} {r.unite}</span></span>
                    <span className="flex items-center gap-3">
                      <span className="tabular" style={{ color: "var(--tm-gold)" }}>{MAD(ingCost(r))}</span>
                      <button type="button" onClick={() => setForm({ ...form, recette: form.recette.filter((_, x) => x !== idx) })} className="text-[var(--tm-muted)] hover:text-[#F44336]"><X size={14} /></button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="tm-card p-4 mb-4" data-testid="product-cost-summary" style={{ background: "var(--tm-bg)" }}>
            <div className="flex justify-between text-sm py-1"><span className="text-[var(--tm-muted)]">Coût ingrédients</span><span className="tabular">{MAD(coutIngredients)}</span></div>
            <div className="flex justify-between text-sm py-1"><span className="text-[var(--tm-muted)]">Coût emballage</span><span className="tabular">{MAD(coutEmballage)}</span></div>
            <div className="flex justify-between text-sm py-1 font-semibold" style={{ borderTop: "1px solid var(--tm-border)" }}><span>Coût de revient unitaire</span><span className="tabular">{MAD(coutRevient)}</span></div>
            <div className="flex justify-between text-sm py-1"><span className="text-[var(--tm-muted)]">Prix de vente</span><span className="tabular">{MAD(prixVente)}</span></div>
            <div className="flex justify-between text-sm py-1 font-semibold" style={{ color: margeColor(margePct) }}><span>Marge</span><span className="tabular">{MAD(margeDH)} ({margePct.toFixed(1)}%)</span></div>
          </div>

          <button type="submit" className="tm-btn w-full mt-2" data-testid="product-save">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
}
